import { createSelector } from "reselect";

import {
  EntityTypesSupportedByIntegrations,
  IntegrationFieldsKey,
} from "@mapmycustomers/shared/types/integrations/types";

import { RootState } from "@app/store/rootReducer";
import IntegrationField from "@app/types/integrations/IntegrationField";

import { getConfiguredSources, getVisibleSourcesByParent } from "./sources";

const integrationsState = (state: RootState) => state.integrations;

export const getLoading = createSelector(integrationsState, ({ loading }) => loading);

export const getLoadingFields = createSelector(
  integrationsState,
  ({ loadingFields }) => loadingFields
);

export const getSaving = createSelector(integrationsState, ({ saving }) => saving);

export const getMetadataUpdating = createSelector(
  integrationsState,
  ({ updatingMetadata }) => updatingMetadata
);

export const getCurrentIntegration = createSelector(
  integrationsState,
  ({ currentIntegration }) => currentIntegration
);

export const getIntegrationViewState = createSelector(
  integrationsState,
  ({ viewState }) => viewState
);

export const getCurrentIntegrationFrequency = createSelector(
  integrationsState,
  ({ currentIntegration }) => currentIntegration!.schedule?.frequency
);

export const getCurrentIntegrationNextRunAt = createSelector(
  integrationsState,
  ({ currentIntegration }) => currentIntegration!.schedule?.nextRunAt
);

export const getIntegrations = createSelector(
  integrationsState,
  ({ integrations }) => integrations
);

export const getIntegrationUsers = createSelector(
  integrationsState,
  ({ integrationUsers }) => integrationUsers
);

export const getIntegrationSyncs = createSelector(
  integrationsState,
  ({ integrationSyncs }) => integrationSyncs
);

export const getLastIntegrationSync = createSelector(
  integrationsState,
  ({ lastIntegrationSync }) => lastIntegrationSync
);

export const getIntegrationFields = createSelector(
  integrationsState,
  ({ integrationFields }) => integrationFields
);

const NO_FIELDS: IntegrationField[] = [];

export const getConfiguredSourcesForRecordType = createSelector(
  [
    getCurrentIntegration,
    (_: RootState, recordType: EntityTypesSupportedByIntegrations) => recordType,
  ],
  (integration, recordType) => getConfiguredSources(integration, recordType)
);

// One pass for every parent, so Step 1 and Step 2 share one memoised result.
export const getVisibleSourcesOfCurrentIntegration = createSelector(
  getCurrentIntegration,
  getVisibleSourcesByParent
);

export const getIntegrationFieldsForKey = createSelector(
  [getIntegrationFields, (_: RootState, key: IntegrationFieldsKey) => key],
  // A shared empty list keeps the result stable for a key with no slot yet.
  (integrationFields, key) => integrationFields[key] ?? NO_FIELDS
);

export const getCustomFieldIntegrationResponse = createSelector(
  integrationsState,
  ({ customFieldIntegrationResponse }) => customFieldIntegrationResponse
);

export const getIntegrationEmailSyncStats = createSelector(
  integrationsState,
  ({ currentIntegration }) => currentIntegration?.metadata?.emailSyncStats ?? true
);

export const getIntegrationNoteSyncing = createSelector(
  integrationsState,
  ({ currentIntegration }) => currentIntegration?.metadata?.noteSyncing
);

export const getIntegrationFileSyncing = createSelector(
  integrationsState,
  ({ currentIntegration }) => currentIntegration?.metadata?.fileSyncing
);

export const isIntegrationChanged = createSelector(
  integrationsState,
  ({ integrationChanged }) => integrationChanged
);

export const getChangedIntegrationFieldKeys = createSelector(
  integrationsState,
  ({ changedIntegrationFieldKeys }) => changedIntegrationFieldKeys
);

export const areIntegrationFieldsChanged = createSelector(
  integrationsState,
  ({ integrationFieldsChanged }) => integrationFieldsChanged
);

export const areIntegrationUsersChanged = createSelector(
  integrationsState,
  ({ integrationUsersChanged }) => integrationUsersChanged
);

export const doNeedToSelectDynamicsInstance = createSelector(
  integrationsState,
  ({ needToSelectDynamicsInstance }) => needToSelectDynamicsInstance
);

export const getLoadingSyncs = createSelector(
  integrationsState,
  ({ syncsLoading }) => syncsLoading
);

export const getLoadingSyncsTotal = createSelector(
  integrationsState,
  ({ integrationSyncsTotal }) => integrationSyncsTotal
);

export const isMatchingFieldsWarningVisible = createSelector(
  integrationsState,
  ({ matchingFieldsWarningVisibility }) => matchingFieldsWarningVisibility
);

export const isBypassLayoutRequirementsWarningVisible = createSelector(
  integrationsState,
  ({ bypassLayoutRequirementsWarningVisibility }) => bypassLayoutRequirementsWarningVisibility
);
